import styled from '@emotion/styled';
import { TitleSmall } from '@shared/components/core/text/Typography';
import { color } from '@shared/constants/Styles';
import { getMacroFallbackName } from '@shared/hooks/helpers/profileUtils';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MacroCommandPreview from './MacroCommandPreview';
import MacroPreviewIcons from './MacroPreviewIcons';

interface IMacroExpanderCardProps {
    macro: MacroDisplayModel;
    startExpanded?: boolean;
    alwaysShowIcons?: boolean;
    children?: ReactNode;
    id?: string;
}

//TODO: update to properly announce expanded state to SR https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
function MacroExpanderCard({ id, macro, startExpanded, alwaysShowIcons = true, children }: IMacroExpanderCardProps) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(startExpanded);
    const iconShow = alwaysShowIcons === true ? false : isExpanded;
    const macroName = macro.name ? macro.name : getMacroFallbackName(macro.commands?.[0]?.command, t);

    return (
        <WrapperMacro>
            <WrapperButton id={id} onClick={() => setIsExpanded(!isExpanded)} aria-pressed={isExpanded}>
                <ActionCardWrapper data-expanded={isExpanded}>
                    <StyledMacroCommandPreview data-expanded={iconShow} commands={macro.commands} iconSize={32} />
                    <TitleSmall>{macroName}</TitleSmall>
                    <MacroPreviewIcons macro={macro} />
                </ActionCardWrapper>
            </WrapperButton>
            {isExpanded && <ExpandedSection>{children}</ExpandedSection>}
        </WrapperMacro>
    );
}

const WrapperMacro = styled.div`
    width: 100%;
`;

const WrapperButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    position: relative;
    width: 100%;
`;
const StyledMacroCommandPreview = styled(MacroCommandPreview)`
    position: absolute;
    left: 24px;
    &[data-expanded='true'] {
        border: none;
        display: none;
    }
`;
const ExpandedSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.theme.color.accentCard};
    animation: all 0.5s ease-in-out;
    border: 1px solid ${color.primary_dark}40;
    border-top: none;
    border-radius: 0 0 10px 10px;
    margin-top: -0.5rem;
    width: 100%;
`;

export const ActionCardWrapper = styled.div`
    box-shadow: none;
    border: 1px solid ${color.primary_dark}40;
    border-radius: 10px;
    width: 100%;
    z-index: 100;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    min-width: 380px;
    padding: 16px;
    background-color: ${(props) => props.theme.color.accentCard};
    color: ${(props) => props.theme.color.text};
    &[data-expanded='true'] {
        box-shadow: none;
        border: 1px solid ${color.primary_dark}40;
        border-bottom: none !important;
        border-radius: 10px 10px 0 0;
    }
    :hover {
        background-color: ${(props) => props.theme.color.accent}40;
        text-decoration: underline;
    }
`;
export default MacroExpanderCard;
